import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, Check, X, RotateCcw, Loader2, Trash2, Flag, Search, AlertTriangle, Ban, ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import FindPhoto from '../components/FindPhoto';
import { findPhotoUrl } from '../lib/wildFinds';

export default function AdminModeration() {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [tab, setTab] = useState('beds');
    const [flags, setFlags] = useState([]);
    const [finds, setFinds] = useState([]);
    const [allPosts, setAllPosts] = useState({ beds: [], finds: [] });
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(null);
    const [uq, setUq] = useState('');
    const [users, setUsers] = useState([]);
    const [userBusy, setUserBusy] = useState(false);
    const [warnFor, setWarnFor] = useState(null);
    const [warnReason, setWarnReason] = useState('');
    const [warnMsg, setWarnMsg] = useState('');
    const [confirmDel, setConfirmDel] = useState(null);

    const searchUsers = async (e) => {
        e?.preventDefault();
        if (!uq.trim()) return;
        setUserBusy(true);
        try { const r = await api.post('/admin/moderation/lookup', { q: uq.trim() }); setUsers(r.data || []); }
        catch { toast.error('Lookup failed'); }
        finally { setUserBusy(false); }
    };

    const sendWarn = async (u) => {
        if (!warnReason.trim()) { toast.error('Add a reason'); return; }
        try {
            await api.post('/admin/moderation/warn', { user_id: u.user_id, reason: warnReason, message: warnMsg });
            toast.success('Warning sent');
            setWarnFor(null); setWarnReason(''); setWarnMsg('');
        } catch { toast.error('Could not send warning'); }
    };

    const banUser = async (u) => {
        try { await api.post('/admin/moderation/ban', { user_id: u.user_id, reason: 'Guideline violation' }); toast.success('User suspended'); searchUsers(); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Could not suspend'); }
    };
    const unbanUser = async (u) => {
        try { await api.post('/admin/moderation/unban', { user_id: u.user_id }); toast.success('Suspension lifted'); searchUsers(); }
        catch { toast.error('Could not unban'); }
    };
    const deleteUser = async (u) => {
        if (confirmDel !== u.user_id) { setConfirmDel(u.user_id); setTimeout(() => setConfirmDel((c) => (c === u.user_id ? null : c)), 3000); return; }
        setConfirmDel(null);
        try { await api.delete(`/admin/moderation/user/${u.user_id}`); toast.success('Account deleted'); setUsers((l) => l.filter((x) => x.user_id !== u.user_id)); }
        catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [b, f, a] = await Promise.all([
                api.get('/admin/community/flags').catch(() => ({ data: [] })),
                api.get('/finds/admin/reports').catch(() => ({ data: [] })),
                api.get('/admin/community/posts').catch(() => ({ data: { beds: [], finds: [] } })),
            ]);
            setFlags(b.data || []);
            setFinds(f.data || []);
            setAllPosts(a.data || { beds: [], finds: [] });
        } catch {
            toast.error('Could not load moderation queue');
        } finally { setLoading(false); }
    }, []);

    useEffect(() => {
        if (user && !user.is_admin) { navigate('/'); return; }
        if (user) load();
    }, [user, load, navigate]);

    const actBed = async (token, action) => {
        setBusy(token);
        try { await api.post(`/admin/community/bed/${token}/moderate`, { action }); toast.success(`Bed: ${action}`); await load(); }
        catch { toast.error('Action failed'); } finally { setBusy(null); }
    };

    const actFind = async (id, action) => {
        setBusy(id);
        try {
            await api.post(`/finds/admin/${id}/moderate`, { action });
            toast.success(`Find: ${action}`);
            setFinds((list) => list.filter((x) => x.id !== id));
        } catch { toast.error('Action failed'); } finally { setBusy(null); }
    };

    const removePost = async (type, id) => {
        setBusy(id);
        try {
            await api.post('/admin/community/delete', { type, id });
            toast.success('Removed from community');
            setAllPosts((p) => ({
                beds: type === 'bed' ? p.beds.filter((x) => x.id !== id) : p.beds,
                finds: type === 'find' ? p.finds.filter((x) => x.id !== id) : p.finds,
            }));
        } catch { toast.error('Could not remove'); } finally { setBusy(null); }
    };

    if (!user || !user.is_admin) return null;

    return (
        <div className="px-5 pt-10 pb-32 max-w-3xl mx-auto" data-testid="admin-moderation">
            <button data-testid="admin-mod-back" onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/dashboard'))} className="inline-flex items-center gap-1 text-sm text-brand-ink3 hover:text-brand-ink mb-6"><ArrowLeft size={14} /> Back</button>
            <p className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3">Admin</p>
            <h1 className="font-serif text-3xl sm:text-4xl text-brand-ink mt-1 mb-5 inline-flex items-center gap-2"><ShieldAlert size={26} /> Moderation queue</h1>

            <div className="flex gap-2 mb-6">
                <button data-testid="modtab-beds" onClick={() => setTab('beds')} className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${tab === 'beds' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>
                    Bed designs {flags.length ? `(${flags.length})` : ''}
                </button>
                <button data-testid="modtab-finds" onClick={() => setTab('finds')} className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${tab === 'finds' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>
                    Foraging finds {finds.length ? `(${finds.length})` : ''}
                </button>
                <button data-testid="modtab-users" onClick={() => setTab('users')} className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${tab === 'users' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>
                    Users
                </button>
                <button data-testid="modtab-all" onClick={() => setTab('all')} className={`flex-1 py-2.5 rounded-full text-sm transition-colors ${tab === 'all' ? 'bg-brand-green text-white' : 'bg-brand-card border border-brand-border text-brand-ink'}`}>
                    All posts
                </button>
            </div>

            {tab === 'users' ? (
                <div data-testid="mod-users">
                    <form onSubmit={searchUsers} className="relative mb-4">
                        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-brand-ink3" />
                        <input data-testid="mod-user-search" value={uq} onChange={(e) => setUq(e.target.value)} placeholder="Search user by email or name…" className="w-full bg-brand-card border border-brand-border rounded-full pl-10 pr-24 py-2.5 text-sm text-brand-ink" />
                        <button type="submit" data-testid="mod-user-search-btn" className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-brand-green text-white text-sm rounded-full px-4 py-1.5">{userBusy ? <Loader2 size={14} className="animate-spin" /> : 'Search'}</button>
                    </form>
                    {users.length === 0 ? (
                        <p className="text-brand-ink3 text-center py-12 text-sm">Search for a user to warn, suspend or delete.</p>
                    ) : (
                        <div className="space-y-2">
                            {users.map((u) => (
                                <div key={u.user_id} className="bg-brand-card border border-brand-border rounded-2xl p-3" data-testid={`mod-user-${u.user_id}`}>
                                    <div className="flex items-center gap-2">
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-brand-ink truncate">{u.name || 'Guest'} {u.banned && <span className="text-[10px] uppercase font-bold text-brand-terracotta">· suspended</span>}</div>
                                            <div className="text-[11px] text-brand-ink3 truncate">{u.email || '—'} · {u.warning_count} warning{u.warning_count === 1 ? '' : 's'}</div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap mt-2">
                                        <ModBtn onClick={() => { setWarnFor(warnFor === u.user_id ? null : u.user_id); setWarnReason(''); setWarnMsg(''); }} icon={AlertTriangle} label="Warn" testid={`mod-warn-${u.user_id}`} />
                                        {u.banned
                                            ? <ModBtn onClick={() => unbanUser(u)} icon={ShieldCheck} label="Unsuspend" good testid={`mod-unban-${u.user_id}`} />
                                            : <ModBtn onClick={() => banUser(u)} icon={Ban} label="Suspend" danger testid={`mod-ban-${u.user_id}`} />}
                                        <ModBtn onClick={() => deleteUser(u)} icon={Trash2} label={confirmDel === u.user_id ? 'Tap again' : 'Delete'} danger testid={`mod-delete-${u.user_id}`} />
                                    </div>
                                    {warnFor === u.user_id && (
                                        <div className="mt-3 space-y-2 border-t border-brand-border pt-3" data-testid={`warn-form-${u.user_id}`}>
                                            <input data-testid="warn-reason" value={warnReason} onChange={(e) => setWarnReason(e.target.value)} placeholder="Reason (e.g. Inappropriate photo)" className="w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" />
                                            <textarea data-testid="warn-message" value={warnMsg} onChange={(e) => setWarnMsg(e.target.value)} rows={2} placeholder="Optional message to the user…" className="w-full bg-brand-bg2 border border-brand-border rounded-xl px-3 py-2 text-sm text-brand-ink" />
                                            <button onClick={() => sendWarn(u)} data-testid={`warn-send-${u.user_id}`} className="w-full bg-brand-ochre text-white rounded-full py-2 text-sm font-medium">Send warning</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : loading ? (
                <div className="text-center py-16"><Loader2 className="animate-spin inline" size={24} /></div>
            ) : tab === 'beds' ? (
                flags.length === 0 ? <p className="text-brand-ink3 text-center py-16" data-testid="empty-beds">No flagged beds. 🎉</p> : (
                    <div className="space-y-3">
                        {flags.map((f) => (
                            <div key={f.share_token} className="bg-brand-card border border-brand-border rounded-3xl p-4" data-testid={`flag-${f.share_token}`}>
                                <div className="flex items-baseline justify-between gap-3 mb-2">
                                    <h3 className="font-serif text-lg text-brand-ink">{f.bed?.name || 'Removed bed'}</h3>
                                    <span className="inline-flex items-center gap-1 text-xs bg-brand-terracotta/15 text-brand-terracotta border border-brand-terracotta/30 rounded-full px-2 py-0.5">{f.flag_count} flag{f.flag_count !== 1 ? 's' : ''}</span>
                                </div>
                                {f.bed?.share_description && <p className="text-sm text-brand-ink3 mb-2">{f.bed.share_description}</p>}
                                {f.reasons.length > 0 && <div className="mb-3 text-xs text-brand-ink3">Reasons: <span className="text-brand-ink">{f.reasons.slice(0, 5).join(' · ')}</span></div>}
                                <div className="flex gap-2 flex-wrap">
                                    <ModBtn onClick={() => actBed(f.share_token, 'restore')} busy={busy === f.share_token} icon={RotateCcw} label="Restore" testid={`btn-restore-${f.share_token}`} />
                                    <ModBtn onClick={() => actBed(f.share_token, 'remove')} busy={busy === f.share_token} icon={X} label="Remove" danger testid={`btn-remove-${f.share_token}`} />
                                    <ModBtn onClick={() => actBed(f.share_token, 'dismiss')} busy={busy === f.share_token} icon={Check} label="Dismiss" good testid={`btn-dismiss-${f.share_token}`} />
                                    <a href={`/community/bed/${f.share_token}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-brand-ink3 underline self-center">View bed</a>
                                </div>
                            </div>
                        ))}
                    </div>
                )
            ) : tab === 'finds' ? (
                finds.length === 0 ? <p className="text-brand-ink3 text-center py-16" data-testid="empty-finds">No reported finds. 🎉</p> : (
                    <div className="space-y-3">
                        {finds.map((f) => {
                            const url = findPhotoUrl(f);
                            return (
                                <div key={f.id} className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden" data-testid={`report-find-${f.id}`}>
                                    {url && <FindPhoto find={f} alt={f.species_name || 'Find photo'} className="w-full h-40 object-cover" loading="lazy" />}
                                    <div className="p-4">
                                        <div className="flex items-baseline justify-between gap-3 mb-1">
                                            <h3 className="font-serif text-lg text-brand-ink">{f.kind === 'mushroom' ? '🍄' : '🌿'} {f.species_name}</h3>
                                            <span className="inline-flex items-center gap-1 text-xs bg-brand-terracotta/15 text-brand-terracotta border border-brand-terracotta/30 rounded-full px-2 py-0.5">
                                                <Flag size={11} /> {f.report_count} {f.hidden ? '· hidden' : ''}
                                            </span>
                                        </div>
                                        {f.note && <p className="text-sm text-brand-ink3">{f.note}</p>}
                                        <p className="text-[10px] text-brand-ink3 mt-1">by {f.author}</p>
                                        {f.reasons.length > 0 && <div className="mt-2 text-xs text-brand-ink3">Reasons: <span className="text-brand-ink">{f.reasons.slice(0, 5).join(' · ')}</span></div>}
                                        <div className="flex gap-2 flex-wrap mt-3">
                                            <ModBtn onClick={() => actFind(f.id, 'restore')} busy={busy === f.id} icon={RotateCcw} label="Restore" testid={`btn-find-restore-${f.id}`} />
                                            <ModBtn onClick={() => actFind(f.id, 'remove')} busy={busy === f.id} icon={Trash2} label="Remove" danger testid={`btn-find-remove-${f.id}`} />
                                            <ModBtn onClick={() => actFind(f.id, 'dismiss')} busy={busy === f.id} icon={Check} label="Dismiss reports" good testid={`btn-find-dismiss-${f.id}`} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            ) : (
                (allPosts.beds.length + allPosts.finds.length) === 0 ? <p className="text-brand-ink3 text-center py-16" data-testid="empty-all">No community posts yet.</p> : (
                    <div className="space-y-6" data-testid="all-posts">
                        {allPosts.beds.length > 0 && (
                            <div>
                                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">🌱 Shared beds ({allPosts.beds.length})</h2>
                                <div className="space-y-3">
                                    {allPosts.beds.map((b) => (
                                        <div key={b.id} className="bg-brand-card border border-brand-border rounded-3xl p-4" data-testid={`all-bed-${b.id}`}>
                                            <div className="flex items-baseline justify-between gap-3 mb-1">
                                                <h3 className="font-serif text-lg text-brand-ink truncate">{b.name || 'Bed'}</h3>
                                                <span className="text-[10px] text-brand-ink3 flex-shrink-0">{(b.created_at || '').slice(0, 10)}</span>
                                            </div>
                                            {b.description && <p className="text-sm text-brand-ink3">{b.description}</p>}
                                            <p className="text-[10px] text-brand-ink3 mt-1">by {b.author || '—'}</p>
                                            <div className="flex gap-2 flex-wrap mt-3">
                                                <ModBtn onClick={() => removePost('bed', b.id)} busy={busy === b.id} icon={Trash2} label="Remove" danger testid={`all-bed-remove-${b.id}`} />
                                                <a href={`/community/bed/${b.share_token}`} target="_blank" rel="noreferrer" className="ml-auto text-xs text-brand-ink3 underline self-center">View bed</a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                        {allPosts.finds.length > 0 && (
                            <div>
                                <h2 className="text-[10px] uppercase tracking-widest font-semibold text-brand-ink3 mb-3">🌿 Shared finds ({allPosts.finds.length})</h2>
                                <div className="space-y-3">
                                    {allPosts.finds.map((f) => {
                                        const url = findPhotoUrl(f);
                                        return (
                                            <div key={f.id} className="bg-brand-card border border-brand-border rounded-3xl overflow-hidden" data-testid={`all-find-${f.id}`}>
                                                {url && <FindPhoto find={f} alt={f.species_name || 'Find photo'} className="w-full h-40 object-cover" loading="lazy" />}
                                                <div className="p-4">
                                                    <div className="flex items-baseline justify-between gap-3 mb-1">
                                                        <h3 className="font-serif text-lg text-brand-ink">{f.kind === 'mushroom' ? '🍄' : '🌿'} {f.species_name}</h3>
                                                        <span className="text-[10px] text-brand-ink3 flex-shrink-0">{(f.created_at || '').slice(0, 10)}</span>
                                                    </div>
                                                    {f.note && <p className="text-sm text-brand-ink3">{f.note}</p>}
                                                    <p className="text-[10px] text-brand-ink3 mt-1">by {f.author || '—'}</p>
                                                    <div className="flex gap-2 flex-wrap mt-3">
                                                        <ModBtn onClick={() => removePost('find', f.id)} busy={busy === f.id} icon={Trash2} label="Remove" danger testid={`all-find-remove-${f.id}`} />
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )
            )}
        </div>
    );
}

function ModBtn({ onClick, busy, icon: Icon, label, danger, good, testid }) {
    const cls = danger
        ? 'bg-brand-terracotta/10 text-brand-terracotta border-brand-terracotta/30 hover:bg-brand-terracotta/20'
        : good
            ? 'bg-brand-green/10 text-brand-green border-brand-green/30 hover:bg-brand-green/20'
            : 'bg-brand-card border-brand-border hover:bg-brand-bg2';
    return (
        <button onClick={onClick} disabled={busy} data-testid={testid} className={`inline-flex items-center gap-1 border rounded-full px-3 py-1.5 text-xs disabled:opacity-50 ${cls}`}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Icon size={12} />} {label}
        </button>
    );
}
